import React, { useState, useEffect } from 'react';
import { TableRow as MuiTableRow, TableCell, Collapse } from '@mui/material';
import { ReferralData, DeduplicationMatch } from '../types';
import ExpandedRowContent from './ExpandedRowContent';
import { renderCellValue, ColumnConfig } from './cellRenderers';
import { ExpandedFieldConfig } from '../Table';
import { STATUS } from '../../LeadManagement/constants';
import { sendToCC } from '../../../services/leadManagementApi';
import { buildNewRecordPayload } from '../utils/buildSendToCCPayload';
import { useSelector } from 'react-redux';
import { ccPatientApi } from '../../../features/lma/ccPatientApi';
import { LeadManagementFields } from '../../LeadManagement/LeadManagement.types';
import ConfirmationDialog from './ConfirmationDialog';

interface TableRowProps {
  row: ReferralData;
  columns: ColumnConfig[];
  onSelectRow: (id: string) => void;
  onPatientCCIdChange?: (id: string, newValue: string) => void;
  onStatusChange?: (id: string, newValue: string) => void;
  currentUser: string;
  lockedRecordsCount: number;
  onLockToggle?: (id: string) => void;
  enableExpandableRows: boolean;
  onOpenMergeModal?: (
    match: DeduplicationMatch | undefined,
    row: ReferralData,
    setters?: {
      setIsSending?: (value: boolean) => void;
      setShowDialog?: (value: boolean) => void;
      setDialogMode?: (value: 'confirm' | 'result') => void;
      setResultMessage?: (value: string) => void;
      setResultType?: (value: 'success' | 'error') => void;
      onRemoveRecord?: (patientId: string) => void;
    }
  ) => void;
  hideDedupeSelectColumn?: boolean;
  hideSendToCCButton?: boolean;
  isProcessedTab?: boolean;
  expandedConfig?: ExpandedFieldConfig;
  isExpanded?: boolean; // Controlled expansion state from parent
  onToggleExpand?: () => void; // Handler to toggle expansion
  onRemoveRecord?: (patientId: string) => void;
  confirmationModalMessages?: LeadManagementFields;
}

const TableRow: React.FC<TableRowProps> = ({
  row,
  columns,
  onSelectRow,
  onPatientCCIdChange,
  onStatusChange,
  currentUser,
  lockedRecordsCount,
  onLockToggle,
  enableExpandableRows,
  onOpenMergeModal,
  hideDedupeSelectColumn = false,
  hideSendToCCButton = false,
  isProcessedTab = false,
  expandedConfig,
  isExpanded = false, // Receive controlled expansion state
  onToggleExpand, // Receive toggle handler
  onRemoveRecord,
  confirmationModalMessages,
}) => {
  // Use controlled state if provided, otherwise use internal state (for backwards compatibility)
  const [internalRowOpen, setInternalRowOpen] = useState(false);
  // Use isExpanded when onToggleExpand is provided (controlled mode)
  const rowOpen = onToggleExpand ? isExpanded : internalRowOpen;
  const [expandedSection, setExpandedSection] = useState<
    'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  >(row.deduplication && row.deduplication.length > 0 ? 'deduplication' : null);
  const [editedCCId, setEditedCCId] = useState(row.patientCCId);
  const [statusValue, setStatusValue] = useState(row.dedupeStatus || row.status || '');
  const [ccIdError, setCcIdError] = useState<string>('');
  const [newComment, setNewComment] = useState<string>('');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'confirm' | 'result'>('result');
  const [resultMessage, setResultMessage] = useState('');
  const [resultType, setResultType] = useState<'success' | 'error'>('success');
  const [isSending, setIsSending] = useState(false);

  // Get CC Patient IDs from deduplication matches
  const ccPatientIds = (row.deduplication || [])
    .map((d) => d.ccPatientReferenceId)
    .filter((id): id is string => !!id);

  // Access cached CC Patient data from Redux store using RTK Query selector
  const cachedCCPatientsData = useSelector((state: any) => {
    if (ccPatientIds.length === 0) return {};
    const selectResult = ccPatientApi.endpoints.getCCPatientsByIds.select(ccPatientIds)(state);
    return selectResult?.data || {};
  });

  // Get valid dedupe CC Ref IDs from the deduplication array or dedupeCCRefIds field
  const validDedupeCCRefIds =
    row.deduplication?.map((d) => d.ccPatientReferenceId) || row.dedupeCCRefIds || [];

  // Auto-select the first deduplication record if status is Auto-Merge and no record is selected
  const getInitialSelectedDedupeRecord = () => {
    if (statusValue === STATUS.AUTO_MERGE && validDedupeCCRefIds.length > 0) {
      return validDedupeCCRefIds[0];
    }
    return '';
  };

  const [selectedDedupeRecord, setSelectedDedupeRecord] = useState<string>(
    getInitialSelectedDedupeRecord()
  );

  // Sync state with row prop changes when row data changes (e.g., after filtering)
  useEffect(() => {
    setEditedCCId(row.patientCCId);
    setStatusValue(row.dedupeStatus || '');
  }, [row.id, row.patientCCId, row.dedupeStatus]);

  // Auto-select the first deduplication record when status changes to Auto-Merge
  useEffect(() => {
    if (
      statusValue === STATUS.AUTO_MERGE &&
      !selectedDedupeRecord &&
      validDedupeCCRefIds.length > 0
    ) {
      setSelectedDedupeRecord(validDedupeCCRefIds[0]);
    }
  }, [statusValue, selectedDedupeRecord, validDedupeCCRefIds]);

  // Validate Patient CC ID
  const validateCCId = (ccId: string, status: string): string => {
    // If status is New-Record, CC ID should be blank
    if (status === STATUS.NEW_RECORD && ccId.trim() !== '') {
      return 'CC Patient ID must be blank for New Record status';
    }

    // If status is Auto-Merge, CC ID must not be blank
    if (status === STATUS.AUTO_MERGE && ccId.trim() === '') {
      return 'CC Patient ID is required for Auto Merge status';
    }

    // Validate CC ID against valid dedupe CC Ref IDs if entered
    if (ccId.trim() !== '' && validDedupeCCRefIds.length > 0) {
      if (!validDedupeCCRefIds.includes(ccId.trim())) {
        return `Invalid CC ID. Must be one of: ${validDedupeCCRefIds.join(', ')}`;
      }
    }

    return '';
  };

  // Check if checkbox should be disabled
  // const isCheckboxDisabled = (): boolean => {
  //   // Disable if locked by another user
  //   if (row.lockedBy && row.lockedBy !== currentUser) {
  //     return true;
  //   }

  //   // Disable if status is manual review
  //   if (statusValue === STATUS.MANUAL_REVIEW_REQUIRED) {
  //     return true;
  //   }

  //   // Disable if status is Auto-Merge and CC ID is blank or invalid
  //   if (statusValue === STATUS.AUTO_MERGE) {
  //     const error = validateCCId(editedCCId, statusValue);
  //     if (error) {
  //       return true;
  //     }
  //   }

  //   return false;
  // };

  const getRowBgColor = () => {
    // If locked by another user, show light red background
    if (row.lockedBy && row.lockedBy !== currentUser) {
      return '#FFE5E5'; // Light red for locked records
    }

    // Use leadType to determine row background color
    if (row.leadType) {
      switch (row.leadType) {
        case 'HotLead':
          return '#FFE5E5'; // Amber
        case 'WarmLead':
          return '#FFF4E5'; // Yellow
        case 'ColdLead':
          return '#E5F2FF'; // Blue
        default:
          return 'transparent';
      }
    }

    return 'transparent';
  };

  const rowBgColor = getRowBgColor();

  const handleCloseDialog = () => {
    if (dialogMode === 'result' && resultType === 'success' && onRemoveRecord) {
      onRemoveRecord(row.patientId);
    }
    setShowDialog(false);
  };

  const handleConfirmAction = async () => {
    if (dialogMode === 'confirm') {
      setShowDialog(false);
      await performSendToCC();
    } else {
      handleCloseDialog();
    }
  };

  const handleCancelAction = () => {
    setShowDialog(false);
  };

  const performSendToCC = async () => {
    setIsSending(true);

    try {
      const currentStatus = statusValue || row.status;

      if (currentStatus === STATUS.AUTO_MERGE) {
        if (selectedDedupeRecord) {
          const selectedMatch = row.deduplication?.find(
            (match) => match.ccPatientReferenceId === selectedDedupeRecord
          );

          if (selectedMatch) {
            const enrichedData = cachedCCPatientsData[selectedMatch.ccPatientReferenceId];

            if (onOpenMergeModal) {
              onOpenMergeModal(enrichedData, row, {
                setIsSending,
                setShowDialog,
                setDialogMode,
                setResultMessage,
                setResultType,
                onRemoveRecord,
              });
            }
          }

          // if (selectedMatch) {
          //   const enrichedData = cachedCCPatientsData[selectedMatch.ccPatientReferenceId];

          //   if (!enrichedData) {
          //     setIsSending(false);
          //     setResultType('error');
          //     setResultMessage(
          //       confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
          //         'Patient data not available. Please expand the row first to load the data.'
          //     );
          //     setDialogMode('result');
          //     setShowDialog(true);
          //     return;
          //   }

          //   const payload = buildAutoMergePayload(
          //     row,
          //     statusValue,
          //     enrichedData,
          //     selectedDedupeRecord
          //   );
          //   const response = await sendToCC(payload);
          //   if (response.success) {
          //     setIsSending(false);
          //     setResultType('success');
          //     const successMessage =
          //       confirmationModalMessages?.ConfirmationModalSuccessMessage?.value ||
          //       'Record sent to CC successfully (Auto-Merge)';
          //     setResultMessage(successMessage.replace('[ccId]', response?.ccId));
          //     setDialogMode('result');
          //     setShowDialog(true);
          //   } else {
          //     setIsSending(false);
          //     setResultType('error');
          //     setResultMessage(
          //       confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
          //         response.message ||
          //         'Failed to send record to CC'
          //     );
          //     setDialogMode('result');
          //     setShowDialog(true);
          //   }
          // }
        }
      } else if (currentStatus === STATUS.NEW_RECORD) {
        const payload = buildNewRecordPayload(row, statusValue);
        const response = await sendToCC(payload);

        if (response.success) {
          setIsSending(false);

          const resources = response.resources || {};
          const resourceTypes = [
            { key: 'conditions', label: 'Conditions' },
            { key: 'procedures', label: 'Procedures' },
            { key: 'medications', label: 'Medications' },
            { key: 'immunizations', label: 'Immunizations' },
            { key: 'allergies', label: 'Allergies' },
            { key: 'familyMemberHistory', label: 'Family Member History' },
            { key: 'devices', label: 'Devices' },
          ];

          const failedResources: string[] = [];
          const errorMessages: string[] = [];
          let hasSkippedResources = false;

          resourceTypes.forEach(({ key, label }) => {
            const resourceData = resources[key];
            if (resourceData && resourceData !== null) {
              if (resourceData.ccError) {
                failedResources.push(label);
                errorMessages.push(resourceData.ccError);
              }
              if (resourceData.hasSkippedResources === true) {
                hasSkippedResources = true;
              }
            }
          });

          const hasResourceError = failedResources.length > 0;

          // Build skipped resources message
          const skippedResourceMessage = hasSkippedResources
            ? 'Some Resources were skipped, check the full CDP profile in processed tab for more details.'
            : '';

          if (hasResourceError) {
            setResultType('success');
            const partialSuccessMessage =
              confirmationModalMessages?.ConfirmationModalPartialSuccessMessage?.value || '-';

            const failedResourcesList = failedResources.join(', ');
            const combinedErrors = errorMessages.join(', ');

            let formattedMessage = partialSuccessMessage
              .replace('[resources]', failedResourcesList)
              .replace('[ccError]', combinedErrors)
              .replace('[skippedResources]', skippedResourceMessage);

            // Remove empty lines caused by empty placeholder replacement
            formattedMessage = formattedMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(formattedMessage);
          } else {
            setResultType('success');
            let successMessage =
              confirmationModalMessages?.ConfirmationModalSuccessMessage?.value ||
              'Record sent to CC successfully';
            successMessage = successMessage
              .replace('[ccId]', response?.ccId)
              .replace('[skippedResources]', skippedResourceMessage);

            // Remove empty lines caused by empty placeholder replacement
            successMessage = successMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(successMessage);
          }
          setDialogMode('result');
          setShowDialog(true);
        } else {
          setIsSending(false);
          setResultType('error');
          setResultMessage(
            confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
              response.message ||
              'Failed to send record to CC'
          );
          setDialogMode('result');
          setShowDialog(true);
        }
      } else {
        setIsSending(false);
        setResultType('error');
        setResultMessage(
          confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
            'Send to CC is only available for new or auto-merge status'
        );
        setDialogMode('result');
        setShowDialog(true);
      }
    } catch (error: any) {
      setIsSending(false);
      setResultType('error');
      setResultMessage(
        confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
          error?.response?.data?.message ||
          error.message ||
          'Unknown error occurred'
      );
      setDialogMode('result');
      setShowDialog(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendToCCClick = async () => {
    const currentStatus = statusValue || row.status;

    if (currentStatus === STATUS.NEW_RECORD) {
      setDialogMode('confirm');
      setShowDialog(true);
    } else {
      await performSendToCC();
    }
  };

  return (
    <React.Fragment>
      <MuiTableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          cursor: enableExpandableRows ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          '&:hover td': {
            backgroundColor: `${rowBgColor} !important`,
          },
        }}
        onClick={() => {
          if (enableExpandableRows) {
            // Use controlled toggle if provided, otherwise use internal state
            if (onToggleExpand) {
              onToggleExpand();
            } else {
              setInternalRowOpen(!internalRowOpen);
            }
          }
        }}
      >
        {columns.map((column) => {
          return (
            <TableCell
              key={column.id}
              align={column.align || 'left'}
              padding={column.id === 'select' ? 'checkbox' : 'normal'}
              sx={{
                width: column.width,
                maxWidth: column.maxWidth || 'none',
                minWidth: 200,
                backgroundColor: rowBgColor,
                wordWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {renderCellValue({
                column,
                row,
                rowOpen,
                setRowOpen: onToggleExpand || (() => setInternalRowOpen(!internalRowOpen)),
                enableExpandableRows,
                onSelectRow,
                isCheckboxDisabled: true,
                editedCCId,
                setEditedCCId,
                statusValue,
                setStatusValue,
                ccIdError,
                setCcIdError,
                validateCCId,
                onPatientCCIdChange,
                onStatusChange,
                currentUser,
                lockedRecordsCount,
                onLockToggle,
                onOpenMergeModal,
                hideSendToCCButton,
              })}
            </TableCell>
          );
        })}
      </MuiTableRow>
      {enableExpandableRows && (
        <MuiTableRow sx={{ backgroundColor: '#f8f9fa' }}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
            <Collapse in={rowOpen} timeout="auto" unmountOnExit>
              <ExpandedRowContent
                row={row}
                expandedSection={expandedSection}
                setExpandedSection={setExpandedSection}
                newComment={newComment}
                setNewComment={setNewComment}
                onOpenMergeModal={onOpenMergeModal}
                onSendToCC={hideSendToCCButton ? undefined : handleSendToCCClick}
                isSending={isSending}
                selectedDedupeRecord={selectedDedupeRecord}
                setSelectedDedupeRecord={setSelectedDedupeRecord}
                hideDedupeSelectColumn={hideDedupeSelectColumn}
                isProcessedTab={isProcessedTab}
                expandedConfig={expandedConfig}
                statusValue={statusValue}
              />
            </Collapse>
          </TableCell>
        </MuiTableRow>
      )}

      <ConfirmationDialog
        open={showDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        mode={dialogMode}
        message={
          dialogMode === 'confirm'
            ? confirmationModalMessages?.ConfirmText?.value ||
              'Are you sure you want to send this record to CC?'
            : resultMessage
        }
        title="Confirm Send to CC"
        confirmButtonText={
          dialogMode === 'confirm'
            ? confirmationModalMessages?.ConfirmButtonText?.value || 'Yes'
            : 'Done'
        }
        cancelButtonText={confirmationModalMessages?.ConfirmDismissButtonText?.value || 'Cancel'}
      />
    </React.Fragment>
  );
};

export default TableRow;
